import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Truck, RefreshCw, LogOut, Flame, PhoneCall, CircleCheck, Users, Search, X, Trophy, Shield, UserCircle2 } from "lucide-react";
import { toast } from "sonner";
import { getLeads, getStats, syncLeads, getConfig, logoutApi, getPayments, getLeaderboardDataset } from "../lib/api";
import { LeadCard } from "./LeadCard";
import { sortLeads, isCallbackDue } from "../lib/callback";
import { TodayCallbacks } from "./TodayCallbacks";
import { Leaderboard } from "./Leaderboard";
import { Payments } from "./Payments";
import { RecruiterPayments } from "./RecruiterPayments";
import { Recruiters } from "./Recruiters";
import { OverduePaymentsBanner } from "./OverduePaymentsBanner";
import { weekWinner } from "../lib/leaderboard";

const BASE_PAGES = [
  { value: "board", label: "Board", match: (l) => !l.archived },
  { value: "applied", label: "Applied", match: (l) => !l.archived && l.stage === "applied" },
  { value: "approved", label: "Approved", match: (l) => !l.archived && l.stage === "approved" },
  { value: "hired", label: "Hired", match: (l) => !l.archived && l.stage === "hired" },
  { value: "archived", label: "Archived", match: (l) => l.archived },
  { value: "leaderboard", label: "Leaderboard" },
  { value: "payments", label: "Payments" },
];

const Metric = ({ icon: Icon, label, value, color, testid }) => (
  <div data-testid={testid} className="bg-[#13161C] border border-[#262C38] rounded-md p-3 sm:p-4">
    <div className="flex items-center gap-2 mb-1">
      <Icon className="h-4 w-4" style={{ color }} />
      <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400">{label}</span>
    </div>
    <div className="font-heading text-3xl font-extrabold text-white leading-none">{value}</div>
  </div>
);

export const Board = ({ user, onLogout }) => {
  const isAdmin = user?.role === "admin";
  const PAGES = isAdmin ? [...BASE_PAGES, { value: "recruiters", label: "Recruiters" }] : BASE_PAGES;
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, hot: 0, uncalled: 0, approved: 0 });
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState("board");
  const [query, setQuery] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [slackConnected, setSlackConnected] = useState(true);
  const [hotAlert, setHotAlert] = useState([]);
  const [recruiterPayments, setRecruiterPayments] = useState(null);
  const [leaderboardLeads, setLeaderboardLeads] = useState([]);
  const [, setTick] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 60000);
    return () => clearInterval(t);
  }, []);

  const refresh = useCallback(async () => {
    const [l, s, ld] = await Promise.all([getLeads(), getStats(), getLeaderboardDataset()]);
    setLeads(l.data);
    setStats(s.data);
    setLeaderboardLeads(ld.data);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const cfg = await getConfig();
        setSlackConnected(cfg.data.slack_connected);
        await refresh();
      } catch {
        onLogout();
      } finally {
        setLoading(false);
      }
    })();
  }, [refresh, onLogout]);

  // For recruiters, keep a local payments cache so the Payments tab renders instantly.
  useEffect(() => {
    if (isAdmin || page !== "payments") return;
    getPayments().then(({ data }) => setRecruiterPayments(data)).catch(() => setRecruiterPayments([]));
  }, [isAdmin, page]);

  const doSync = async () => {
    setSyncing(true);
    try {
      const { data } = await syncLeads();
      await refresh();
      const src = data.source === "sample" ? " (sample data \u2014 connect Slack for live leads)" : "";
      toast.success(`${data.inserted} new lead${data.inserted === 1 ? "" : "s"} pulled${src}`);
      if (data.new_hot_count > 0) {
        setHotAlert(data.new_hot);
        toast(`${data.new_hot_count} new HOT lead${data.new_hot_count === 1 ? "" : "s"} \u2014 call first!`, {
          icon: <Flame className="h-4 w-4 text-red-400" />,
          duration: 7000,
        });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const logout = async () => {
    await logoutApi();
    onLogout();
  };

  const active = leads.filter((l) => !l.archived);
  const winners = { current: weekWinner(leaderboardLeads, 0), last: weekWinner(leaderboardLeads, -1) };
  const dueCount = active.filter(isCallbackDue).length;
  const pageDef = PAGES.find((p) => p.value === page);
  const filtered = sortLeads(leads).filter((l) => {
    if (pageDef.match && !pageDef.match(l)) return false;
    if (filter === "hot" && l.priority !== "hot") return false;
    if (filter === "callbacks" && !isCallbackDue(l)) return false;
    if (filter === "uncalled" && (l.tags || []).includes("called")) return false;
    if (query) {
      const q = query.toLowerCase();
      if (!(`${l.full_name} ${l.notes} ${l.phone || ""}`.toLowerCase().includes(q))) return false;
    }
    return true;
  });

  const FilterBtn = ({ value, label, testid }) => (
    <button
      data-testid={testid}
      onClick={() => setFilter(value)}
      className={`text-xs font-mono uppercase tracking-wide px-3 py-1.5 rounded border transition-colors ${filter === value ? "bg-[#F95722] text-white border-[#F95722]" : "bg-transparent text-slate-400 border-[#262C38] hover:border-[#384152]"}`}
    >
      {label}
    </button>
  );

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#0B0C0E]/90 backdrop-blur-md border-b border-[#262C38] px-4 py-3">
        <div className="max-w-5xl mx-auto flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded bg-[#F95722] flex items-center justify-center">
              <Truck className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 data-testid="dashboard-header-title" className="font-heading text-xl sm:text-2xl font-extrabold uppercase tracking-tight text-white leading-none">
                Hylas Matrix
              </h1>
              <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                Call the hottest drivers first
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span data-testid="identity-chip" className="inline-flex items-center gap-1.5 font-mono text-[11px] text-slate-200 border border-[#262C38] rounded px-2 sm:px-2.5 py-1">
              {isAdmin ? <Shield className="h-3 w-3 text-amber-300" /> : <UserCircle2 className="h-3 w-3 text-sky-300" />}
              <span className="truncate max-w-[90px] sm:max-w-[140px]">{user?.name}</span>
              <span className="hidden sm:inline font-bold uppercase tracking-widest text-[9px] text-slate-400">{isAdmin ? "Admin" : "Recruiter"}</span>
            </span>
            {isAdmin && (
              <button
                data-testid="sync-slack-btn"
                onClick={doSync}
                disabled={syncing}
                className="inline-flex items-center gap-2 bg-[#F95722] hover:bg-[#FF6B38] text-white font-bold py-2 px-3 sm:px-4 rounded text-xs uppercase tracking-wide transition-transform active:scale-95 disabled:opacity-60"
              >
                <RefreshCw className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} />
                <span className="hidden sm:inline">Sync now</span>
              </button>
            )}
            <button data-testid="logout-btn" onClick={logout} className="p-2 rounded border border-[#262C38] text-slate-400 hover:text-white hover:border-[#384152] transition-colors">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-5">
        <AnimatePresence>
          {hotAlert.length > 0 && (
            <motion.div
              data-testid="hot-alert-banner"
              initial={{ opacity: 0, y: -10, height: 0 }}
              animate={{ opacity: 1, y: 0, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="mb-4 flex items-center gap-3 bg-red-950/40 border border-red-500/50 rounded-md px-4 py-3 hot-glow"
            >
              <Flame className="h-5 w-5 text-red-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <span className="font-heading font-bold uppercase tracking-wide text-red-300 text-sm">
                  {hotAlert.length} new hot lead{hotAlert.length === 1 ? "" : "s"} just landed
                </span>
                <span className="block text-xs text-slate-300 truncate">Call first: {hotAlert.join(", ")}</span>
              </div>
              <button
                data-testid="hot-alert-dismiss"
                onClick={() => setHotAlert([])}
                className="p-1 text-slate-400 hover:text-white transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {!slackConnected && (
          <div className="mb-4 text-xs font-mono text-amber-300 bg-amber-950/30 border border-amber-700/40 rounded px-3 py-2">
            Slack not connected yet &mdash; "Sync now" loads realistic sample drivers so you can try the board. Add your Slack bot token to pull live #{"lead-handoff"} leads.
          </div>
        )}

        {/* Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
          <Metric testid="metric-total" icon={Users} label="Total Leads" value={stats.total} color="#94A3B8" />
          <Metric testid="metric-hot" icon={Flame} label="Hot Priority" value={stats.hot} color="#F87171" />
          <Metric testid="metric-uncalled" icon={PhoneCall} label="Pending Calls" value={stats.uncalled} color="#FBBF24" />
          <Metric testid="metric-approved" icon={CircleCheck} label="Approved" value={stats.approved} color="#34D399" />
          <Metric testid="metric-hired" icon={Trophy} label="Hired" value={stats.hired ?? 0} color="#FBBF24" />
        </div>

        {isAdmin && <OverduePaymentsBanner refreshKey={page} onOpen={() => setPage("payments")} />}
        <TodayCallbacks leads={leads} />

        {/* Pages */}
        <div className="flex gap-1 mb-4 overflow-x-auto border-b border-[#262C38]" data-testid="page-tabs">
          {PAGES.map((p) => (
            <button key={p.value} data-testid={`page-tab-${p.value}`} onClick={() => setPage(p.value)}
              className={`shrink-0 px-3 py-2 text-xs font-mono uppercase tracking-wide border-b-2 -mb-px transition-colors ${page === p.value ? "border-[#F95722] text-white" : "border-transparent text-slate-500 hover:text-slate-300"}`}>
              {p.label}{p.match ? ` (${leads.filter(p.match).length})` : ""}
            </button>
          ))}
        </div>

        {page === "payments" ? (
          isAdmin ? <Payments /> : <RecruiterPayments payments={recruiterPayments} />
        ) : page === "recruiters" ? (
          <Recruiters me={user} />
        ) : page === "leaderboard" ? (
          <Leaderboard />
        ) : (
        <>
        {/* Toolbar */}
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <FilterBtn value="all" label="All" testid="filter-all-btn" />
          <FilterBtn value="hot" label="Hot only" testid="filter-hot-only-btn" />
          <FilterBtn value="uncalled" label="Un-called" testid="filter-uncalled-only-btn" />
          <FilterBtn value="callbacks" label={`Callbacks due (${dueCount})`} testid="filter-callbacks-due-btn" />
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <input
              data-testid="search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, phone, notes"
              className="w-full bg-[#13161C] border border-[#262C38] rounded px-9 py-2 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-[#F95722]"
            />
          </div>
        </div>

        {/* Lead stream */}
        {loading ? (
          <div className="text-center py-20 text-slate-500 font-mono text-sm">Loading board...</div>
        ) : filtered.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-16 border border-dashed border-[#262C38] rounded-lg"
          >
            <Truck className="h-10 w-10 text-slate-600 mx-auto mb-3" />
            <p data-testid="empty-state" className="text-slate-400 text-sm">{page === "board" ? (isAdmin ? "No leads yet." : "No leads posted by you yet.") : `No ${pageDef.label.toLowerCase()} leads.`}</p>
            {page === "board" && isAdmin && <p className="text-slate-600 text-xs font-mono mt-1">Hit "Sync now" to pull drivers from Slack.</p>}
            {page === "board" && !isAdmin && <p className="text-slate-600 text-xs font-mono mt-1">New leads will appear here as soon as you post a driver in Slack and the admin runs the next sync.</p>}
          </motion.div>
        ) : (
          <div className="grid gap-3 sm:gap-4">
            {filtered.map((lead) => (
              <LeadCard key={lead.id} lead={lead} onUpdated={refresh} winners={winners} />
            ))}
          </div>
        )}
        </>
        )}
      </main>
    </div>
  );
};
